import React from "react";
import moment from "moment";
import { FiPlus } from "react-icons/fi";

// =======================================================
// DayCard
// -------------------------------------------------------
// Displays one calendar day and its appointments.
//
// Responsibilities:
// - Show date, artist and booking status
// - Highlight today and holidays
// - Render appointment cards
// - Handle add and reschedule target clicks
//
// Business actions remain inside Home.js and are passed
// into this component as callbacks.
// =======================================================

const DayCard = ({
  day,
  artistInfo,
  viewMode,
  holidaysNRW,
  rescheduleMode,
  retouchMode = false,
  onPickRetouchDay,
  appointmentToMove,
  todayDayRef,
  onOpenAddAppointment,
  onOpenRescheduleModal,
  renderAppointment,
}) => {
  const retouchTarget = retouchMode && moment(day.day).isSameOrAfter(moment(), 'day');
  const isToday = moment(day.day).isSame(moment(), "day");
  const containerClass =
    viewMode === "grid" ? "date-box" : "artist-day-block";
  const listMode = viewMode !== "grid";

  const dateKey = moment(day.day).format("YYYY-MM-DD");
  const holidayName = holidaysNRW[dateKey];
  const isHoliday = Boolean(holidayName);

  return (
    <div
      ref={isToday ? todayDayRef : null}
      className={`
        ${containerClass}
        ${isToday ? "highlight-today" : ""}
        ${isHoliday ? "highlight-holiday" : ""}
        ${rescheduleMode ? "reschedule-target-day" : ""}
        ${retouchTarget ? "retouch-target-day" : ""}
      `}
      data-day-id={day.dayId}
      tabIndex={retouchTarget ? 0 : undefined}
      aria-label={retouchTarget ? `Nachstechtermin bei ${artistInfo?.artistName || day.artistName} am ${moment(day.day).format('DD.MM.YYYY')} planen` : undefined}
      onKeyDown={(event) => {
        if (retouchTarget && event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          onPickRetouchDay(day);
        }
      }}
      onClickCapture={(event) => {
        if (retouchTarget && !event.target.closest('button, a, input, .appointment-item')) { onPickRetouchDay(day); return; }
        if (!rescheduleMode || !appointmentToMove) return;

        const clickedButtonOrLink = event.target.closest("button, a");
        if (clickedButtonOrLink) return;

        onOpenRescheduleModal(day, artistInfo.artistName);
      }}
    >
      <div className={listMode ? "day-subheading" : "date-header"}>
        <div className="day-info">
          <span
            className="day-led"
            style={{
              backgroundColor:
                day.bookingStatus === "red"
                  ? "red"
                  : day.bookingStatus === "yellow"
                    ? "yellow"
                    : "green",
            }}
            title={`Booking Status: ${day.bookingStatus}`}
          />

          <div className="date-wrapper">
            <span className={listMode ? "date-text" : "day-name"}>
              {moment(day.day).format(
                listMode ? "dddd, D. MMMM" : "ddd, DD.MM.YYYY"
              )}
            </span>

            {isHoliday && (
              <div className="holiday-label">
                {holidayName}
              </div>
            )}
          </div>

          {artistInfo?.artistName && (
            <span className="day-artist-name">
              {artistInfo.artistName}
            </span>
          )}
        </div>

        {!retouchMode && <button
          type="button"
          className="add-appointment-inline"
          onClick={(event) => {
            event.stopPropagation();

            if (!rescheduleMode) {
              onOpenAddAppointment(
                day.dayId,
                day.artistId,
                day.day
              );
            }
          }}
          title="Termin hinzufügen"
        >
          <FiPlus />
        </button>}
      </div>

      <div
        className={`appointments-list ${listMode ? "list-mode" : ""
          }`}
      >
        {(day.appointments || []).length > 0 ? (
          [...day.appointments]
            .sort((a, b) =>
              moment(a.time, "HH:mm:ss").diff(
                moment(b.time, "HH:mm:ss")
              )
            )
            .map((appointment) =>
              renderAppointment(appointment, day)
            )
        ) : (
          <div>
            <em>Keine Termine</em>
          </div>
        )}
      </div>
    </div>
  );
};

export default DayCard;